import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flag, Pencil, Play, Plus, Trash2 } from "lucide-react";

import { Badge, Button, PlayerPicker, TournamentStatusBadge, useConfirm } from "@/ui";
import { BracketLayout } from "@/features/events/BracketView";
import { formatEventDate } from "@/lib/format";
import { noAutofill } from "@/lib/noAutofill";
import {
  useAddTeam,
  useAdminMatchResult,
  useAdminPlayMatch,
  useBracketMatches,
  useBracketTeams,
  useFinishInternalTournament,
  isMatchLive,
  searchPlayers,
  useRemoveTeam,
  useStartInternalTournament,
  useTournament,
  type BracketMatch,
  type BracketTeam,
} from "@/features/queries";

export function AdminTournamentDetailPage() {
  const { id: idStr } = useParams();
  const tournamentId = Number(idStr);
  const navigate = useNavigate();

  const { data: t } = useTournament(tournamentId);
  const { data: teams = [] } = useBracketTeams(tournamentId);
  const { data: matches = [] } = useBracketMatches(tournamentId);

  if (!t) {
    return (
      <div>
        <BackLink />
        <p className="body">Загрузка…</p>
      </div>
    );
  }

  const canEdit = t.status === "draft" || t.status === "registration";
  const canManageTeams = t.status === "draft" || t.status === "registration" || t.status === "full";
  const canStart = canManageTeams && teams.filter((x) => !x.is_bye).length >= 2;
  const canForceFinish = t.status === "in_progress";

  return (
    <div>
      <BackLink />

      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <h1 className="h1" style={{ margin: 0 }}>{t.name}</h1>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <TournamentStatusBadge status={t.status} />
          {canEdit && (
            <Button
              size="sm"
              variant="secondary"
              leading={<Pencil size={14} />}
              onClick={() => navigate(`/admin/tournaments/${tournamentId}/edit`)}
            >
              Изменить
            </Button>
          )}
          {canForceFinish && <ForceFinishButton tournamentId={tournamentId} />}
        </div>
      </header>
      <p className="caption" style={{ color: "var(--fg2)", marginBottom: 16 }}>
        {formatEventDate(t.date_time)} · {t.spot_name} · {formatLabel(t.format)} ·{" "}
        {t.team_size === 2 ? "парный" : "одиночный"} · best-of-{t.best_of}
      </p>

      {/* Section: teams */}
      <Section title={t.team_size === 2 ? "Команды" : "Участники"}>
        <TeamsEditor
          tournamentId={tournamentId}
          teams={teams}
          teamSize={t.team_size}
          readonly={!canManageTeams}
        />

        {canStart && (
          <div style={{ marginTop: 12 }}>
            <StartButton tournamentId={tournamentId} />
          </div>
        )}
      </Section>

      {/* Section: bracket overview */}
      {matches.length > 0 && (
        <Section title={t.format === "round_robin" ? "Таблица" : "Сетка"}>
          <BracketLayout matches={matches} format={t.format} />
        </Section>
      )}

      {/* Section: matches (scoring) */}
      {matches.length > 0 && (
        <Section
          title="Матчи"
          trailing={canForceFinish ? <ForceFinishButton tournamentId={tournamentId} /> : null}
        >
          <MatchList
            tournamentId={tournamentId}
            matches={matches}
            format={t.format}
          />
        </Section>
      )}

      {matches.length === 0 && t.status !== "draft" && t.status !== "registration" && (
        <Section title="Матчи">
          <p className="body" style={{ color: "var(--fg2)" }}>
            Сетка ещё не сгенерирована.
          </p>
        </Section>
      )}
    </div>
  );

  function BackLink() {
    return (
      <button
        onClick={() => navigate("/admin/tournaments")}
        style={{
          background: "transparent", border: 0, color: "var(--fg2)", cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 6, padding: 0, marginBottom: 12,
          fontFamily: "var(--font-body)", fontSize: 13,
        }}
      >
        <ArrowLeft size={14} /> к списку турниров
      </button>
    );
  }
}

function formatLabel(format: string): string {
  switch (format) {
    case "single_elim": return "Single-elim";
    case "double_elim": return "Double-elim";
    case "round_robin": return "Round-robin";
    default: return format;
  }
}

// ---------- Section wrapper ------------------------------------------------
function Section({
  title,
  trailing,
  children,
}: {
  title: string;
  trailing?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: "var(--bg-section)",
        borderRadius: "var(--r-lg)",
        padding: 20,
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <h2 className="h2" style={{ margin: 0 }}>{title}</h2>
        {trailing}
      </div>
      {children}
    </section>
  );
}

function ForceFinishButton({ tournamentId }: { tournamentId: number }) {
  const finish = useFinishInternalTournament();
  const confirm = useConfirm();
  return (
    <Button
      size="sm"
      variant="secondary"
      leading={<Flag size={14} />}
      disabled={finish.isPending}
      onClick={async () => {
        const ok = await confirm({
          title: "Завершить турнир принудительно?",
          description:
            "Статус сменится на «завершён». Незавершённые матчи останутся как есть — рейтинги пересчитаны не будут.",
          confirmLabel: "Завершить",
          tone: "danger",
        });
        if (ok) finish.mutate(tournamentId);
      }}
    >
      {finish.isPending ? "Завершаем…" : "Завершить принудительно"}
    </Button>
  );
}

function StartButton({ tournamentId }: { tournamentId: number }) {
  const start = useStartInternalTournament();
  const confirm = useConfirm();
  return (
    <Button
      leading={<Play size={16} />}
      disabled={start.isPending}
      onClick={async () => {
        const ok = await confirm({
          title: "Стартовать турнир?",
          description:
            "Будет сгенерирована сетка по текущему составу. После старта добавлять или убирать участников уже нельзя.",
          confirmLabel: "Стартовать",
        });
        if (ok) start.mutate(tournamentId);
      }}
    >
      {start.isPending ? "Генерируем сетку…" : "Старт турнира"}
    </Button>
  );
}

// ---------- Teams editor ---------------------------------------------------
// A roster slot is either a registered player (picked via search) or a
// guest (free-form name typed by the admin). `null` means the slot
// hasn't been filled yet. Discriminated by `kind` so the submit handler
// can split them into user_ids vs guest_names for the API.
type Slot =
  | { kind: "user"; userId: number }
  | { kind: "guest"; name: string };

const emptySlots = (size: number): (Slot | null)[] =>
  Array.from({ length: size }, () => null);

function TeamsEditor({
  tournamentId,
  teams,
  teamSize,
  readonly,
}: {
  tournamentId: number;
  teams: BracketTeam[];
  teamSize: number;
  readonly: boolean;
}) {
  const add = useAddTeam(tournamentId);
  const remove = useRemoveTeam(tournamentId);
  const isDoubles = teamSize === 2;
  const [slots, setSlots] = useState<(Slot | null)[]>(() => emptySlots(teamSize));
  // Each slot has its own "is this a guest?" toggle so doubles can mix
  // a real player with a placeholder (e.g. partner showed up but doesn't
  // have the app installed).
  const [slotIsGuest, setSlotIsGuest] = useState<boolean[]>(() =>
    Array.from({ length: teamSize }, () => false),
  );
  const [teamName, setTeamName] = useState("");
  const confirm = useConfirm();

  const usedUserIds = useMemo(() => {
    const set = new Set<number>();
    for (const t of teams) {
      for (const m of t.members) {
        // Guests live only inside one tournament — they can never be
        // re-picked, so don't pollute the exclusion list with them.
        if (!m.is_guest) set.add(m.user_id);
      }
    }
    return set;
  }, [teams]);

  const setSlot = (idx: number, v: Slot | null) =>
    setSlots((prev) => prev.map((s, i) => (i === idx ? v : s)));

  const toggleSlotKind = (idx: number, asGuest: boolean) => {
    setSlotIsGuest((prev) => prev.map((g, i) => (i === idx ? asGuest : g)));
    // Reset the slot — kind change invalidates whatever was picked.
    setSlot(idx, null);
  };

  const allSlotsFilled = slots.every(
    (s) => s !== null && (s.kind === "user" || s.name.trim() !== ""),
  );
  const userIds = slots
    .filter((s): s is Extract<Slot, { kind: "user" }> => s?.kind === "user")
    .map((s) => s.userId);
  const guestNames = slots
    .filter((s): s is Extract<Slot, { kind: "guest" }> => s?.kind === "guest")
    .map((s) => s.name.trim());
  const noDuplicates = new Set(userIds).size === userIds.length;

  const submit = () => {
    if (!allSlotsFilled || !noDuplicates) return;
    add.mutate(
      {
        user_ids: userIds.length ? userIds : undefined,
        guest_names: guestNames.length ? guestNames : undefined,
        name: teamName.trim() || undefined,
      },
      {
        onSuccess: () => {
          setSlots(emptySlots(teamSize));
          setSlotIsGuest(Array.from({ length: teamSize }, () => false));
          setTeamName("");
        },
      },
    );
  };

  // A slot must exclude: players already on another team in this tournament
  // + players picked into sibling slots of the team being built.
  const excludeForSlot = (idx: number): number[] => {
    const set = new Set<number>(usedUserIds);
    slots.forEach((s, i) => {
      if (i !== idx && s?.kind === "user") set.add(s.userId);
    });
    return Array.from(set);
  };

  return (
    <>
      {teams.length === 0 ? (
        <p className="body" style={{ color: "var(--fg2)" }}>Пока никого. Добавь участников.</p>
      ) : (
        <ol style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 }}>
          {teams.map((team, idx) => (
            <li
              key={team.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 12px",
                borderRadius: "var(--r-md)",
                background: team.is_bye ? "var(--bg-secondary)" : "var(--bg)",
                opacity: team.is_bye ? 0.6 : 1,
              }}
            >
              <span className="numeric" style={{ fontSize: 14, color: "var(--fg3)", width: 24 }}>
                {team.seed ?? idx + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <span>{team.name}</span>
                  {/* Admin-only badge — regular players don't need to know
                      a teammate is a placeholder, see feedback memory
                      "Служебные UI-маркеры — только админам". This file
                      is already an admin route, so unconditional render
                      here is the admin gate. */}
                  {team.members.some((m) => m.is_guest) && (
                    <Badge kind="neutral">гость</Badge>
                  )}
                </div>
                {!team.is_bye && team.members.length > 0 && (
                  <div className="caption" style={{ color: "var(--fg2)" }}>
                    {team.members
                      .map((m) => (m.rating ? `${m.first_name ?? m.username ?? "?"} · ${m.rating}` : m.username))
                      .join(" · ")}
                  </div>
                )}
              </div>
              {team.is_bye && <Badge kind="neutral">BYE</Badge>}
              {!readonly && (
                <button
                  onClick={async () => {
                    const ok = await confirm({
                      title: "Убрать участника?",
                      description: `«${team.name}» больше не будет в составе турнира.`,
                      confirmLabel: "Убрать",
                      tone: "danger",
                    });
                    if (ok) remove.mutate(team.id);
                  }}
                  title="Убрать"
                  style={{
                    width: 30, height: 30, borderRadius: "var(--r-md)", border: 0,
                    background: "var(--bg-secondary)", color: "var(--fg-danger)", cursor: "pointer",
                  }}
                >
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ol>
      )}

      {!readonly && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {slots.map((val, idx) => {
            const asGuest = slotIsGuest[idx];
            return (
              <div key={idx} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <label
                    className="caption"
                    style={{ color: "var(--fg2)", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
                  >
                    <input
                      type="checkbox"
                      checked={asGuest}
                      onChange={(e) => toggleSlotKind(idx, e.target.checked)}
                    />
                    Гость
                  </label>
                </div>
                {asGuest ? (
                  <input
                    type="text"
                    value={val?.kind === "guest" ? val.name : ""}
                    onChange={(e) => setSlot(idx, { kind: "guest", name: e.target.value })}
                    placeholder={isDoubles ? `Имя гостя ${idx + 1}` : "Имя гостя"}
                    {...noAutofill}
                    style={{
                      border: "1px solid var(--separator)", borderRadius: "var(--r-md)",
                      padding: "10px 14px", fontSize: 15, background: "var(--bg)", color: "var(--fg1)",
                    }}
                  />
                ) : (
                  <PlayerPicker
                    value={val?.kind === "user" ? val.userId : null}
                    onChange={(id) => setSlot(idx, id === null ? null : { kind: "user", userId: id })}
                    excludeIds={excludeForSlot(idx)}
                    placeholder={isDoubles ? `Игрок ${idx + 1} — поиск по имени` : "Поиск по имени или @"}
                    searchFn={searchPlayers}
                  />
                )}
              </div>
            );
          })}
          {isDoubles && (
            <input
              type="text"
              value={teamName}
              onChange={(e) => setTeamName(e.target.value)}
              placeholder="Название команды (необязательно)"
              {...noAutofill}
              style={{
                border: "1px solid var(--separator)", borderRadius: "var(--r-md)",
                padding: "10px 14px", fontSize: 15, background: "var(--bg)", color: "var(--fg1)",
              }}
            />
          )}
          <Button
            size="sm"
            leading={<Plus size={14} />}
            disabled={!allSlotsFilled || !noDuplicates || add.isPending}
            onClick={submit}
            style={{ alignSelf: "flex-start" }}
          >
            {isDoubles ? "Добавить команду" : "Добавить"}
          </Button>
          {(add.error || remove.error) && (
            <div
              style={{
                background: "rgba(223,63,64,0.1)",
                color: "var(--fg-danger)",
                padding: "8px 12px",
                borderRadius: "var(--r-md)",
                fontSize: 13,
              }}
            >
              {((add.error || remove.error) as Error).message}
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ---------- Match list (admin can set result) ------------------------------
function MatchList({
  tournamentId,
  matches,
  format,
}: {
  tournamentId: number;
  matches: BracketMatch[];
  format: string;
}) {
  const sections = useMemo(() => buildMatchSections(matches, format), [matches, format]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {sections.map((section) => (
        <div key={section.key}>
          <div className="section-header" style={{ padding: "0 0 8px" }}>
            {section.title}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {section.matches.map((m) => (
              <MatchRow key={m.id} tournamentId={tournamentId} match={m} matches={matches} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

interface MatchSection {
  key: string;
  title: string;
  matches: BracketMatch[];
}

/** Group matches by bracket-side + round with human-readable labels. */
function buildMatchSections(matches: BracketMatch[], format: string): MatchSection[] {
  if (format === "round_robin") {
    const rounds = roundsOf(matches);
    return rounds.map(([r, list]) => ({
      key: `rr-${r}`,
      title: `Тур ${r}`,
      matches: list,
    }));
  }

  const upper = matches.filter((m) => m.bracket === "upper");
  const lower = matches.filter((m) => m.bracket === "lower");
  const gf = matches.filter((m) => m.bracket === "grand_final");

  const upperRounds = roundsOf(upper);
  const lowerRounds = roundsOf(lower);
  const isDouble = lower.length > 0 || gf.length > 0;

  const sections: MatchSection[] = [];
  for (const [r, list] of upperRounds) {
    sections.push({
      key: `upper-${r}`,
      title: isDouble ? upperDoubleLabel(r, upperRounds.length) : singleElimLabel(r, upperRounds.length),
      matches: list,
    });
  }
  for (const [r, list] of lowerRounds) {
    sections.push({
      key: `lower-${r}`,
      title: lowerLabel(r, lowerRounds.length),
      matches: list,
    });
  }
  if (gf.length > 0) {
    sections.push({ key: "gf", title: "Гранд-финал", matches: gf });
  }
  return sections;
}

function roundsOf(matches: BracketMatch[]): [number, BracketMatch[]][] {
  const g = new Map<number, BracketMatch[]>();
  for (const m of matches) {
    const list = g.get(m.round) ?? [];
    list.push(m);
    g.set(m.round, list);
  }
  for (const [, list] of g) list.sort((a, b) => a.slot - b.slot);
  return [...g.entries()].sort((a, b) => a[0] - b[0]);
}

function singleElimLabel(round: number, total: number): string {
  const remaining = total - round;
  if (remaining === 0) return "Финал";
  if (remaining === 1) return "Полуфинал";
  if (remaining === 2) return "Четвертьфинал";
  return `Раунд ${round}`;
}

function upperDoubleLabel(round: number, total: number): string {
  const remaining = total - round;
  if (remaining === 0) return "Верхний финал";
  if (remaining === 1) return "Верхний полуфинал";
  return `Верхний раунд ${round}`;
}

function lowerLabel(round: number, total: number): string {
  if (round === total) return "Нижний финал";
  return `Нижний раунд ${round}`;
}

function MatchRow({
  tournamentId,
  match,
  matches,
}: {
  tournamentId: number;
  match: BracketMatch;
  matches: BracketMatch[];
}) {
  const [editing, setEditing] = useState(false);
  const playMutation = useAdminPlayMatch(tournamentId);
  // Auto-dismissing confirmation shown right after a successful Play.
  // We keep it local to the row (instead of pulling in a toast system)
  // so the feedback stays anchored to the match the admin just pressed.
  const [ack, setAck] = useState<string | null>(null);
  useEffect(() => {
    if (!ack) return;
    const t = setTimeout(() => setAck(null), 3500);
    return () => clearTimeout(t);
  }, [ack]);

  const live = isMatchLive(match);

  const stateBadge = (() => {
    if (live) return <Badge kind="success">идёт</Badge>;
    switch (match.state) {
      case "ready": return <Badge kind="brand">готов</Badge>;
      case "confirmed": return <Badge kind="success">завершён</Badge>;
      case "walkover": return <Badge kind="neutral">walkover</Badge>;
      case "waiting_confirm": return <Badge kind="warning">ожидает подтв.</Badge>;
      case "disputed": return <Badge kind="live">спор</Badge>;
      default: return <Badge kind="neutral">ожидает</Badge>;
    }
  })();

  const hasBothTeams = !!(match.team_a && match.team_b && !match.team_a.is_bye && !match.team_b.is_bye);
  const parent = match.next_match_id
    ? matches.find((x) => x.id === match.next_match_id)
    : undefined;
  const parentLocked = !!parent && (parent.state === "confirmed" || parent.state === "walkover");

  const canEnter =
    hasBothTeams && (match.state === "ready" || match.state === "waiting_confirm" || match.state === "disputed");
  const canRewrite = hasBothTeams && match.state === "confirmed" && !parentLocked;
  const isRewrite = match.state === "confirmed";
  const canEditOrEnter = canEnter || canRewrite;
  // Play is the only TG-ping trigger now: bracket progression no
  // longer notifies anyone automatically. Re-pressable while the
  // match is still open (e.g. players didn't show up yet).
  const canPlay = hasBothTeams && (match.state === "ready" || match.state === "waiting_confirm");

  const handlePlay = async () => {
    try {
      const r = await playMutation.mutateAsync(match.id);
      // Sends run in a server-side background task so the button doesn't
      // hang on Telegram, so all we know here is how many rows got
      // queued. The retry-helper covers actual delivery; failures end up
      // in `scheduled_notifications.last_error` for ops to chase.
      const word = r.notified === 1 ? "игроку" : "игрокам";
      setAck(`Уведомление отправляется ${r.notified} ${word}`);
    } catch (e) {
      setAck(`Не вышло: ${(e as Error).message}`);
    }
  };

  return (
    <div
      style={{
        background: live
          ? "rgba(30,122,90,0.10)"
          : "var(--bg-secondary)",
        borderRadius: "var(--r-md)",
        padding: "10px 14px",
        outline: live ? "1.5px solid #1E7A5A" : "none",
        outlineOffset: -1,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <SideLabel team={match.team_a} isWinner={match.winner_team_id === match.team_a?.id} />
        <span className="caption" style={{ color: "var(--fg3)" }}>vs</span>
        <SideLabel team={match.team_b} isWinner={match.winner_team_id === match.team_b?.id} />
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          {stateBadge}
          {canPlay && (
            <Button
              size="sm"
              variant="ghost"
              onClick={handlePlay}
              disabled={playMutation.isPending}
              aria-label="Уведомить игроков о матче"
            >
              <Play size={14} style={{ marginRight: 4 }} />
              {playMutation.isPending ? "…" : live ? "Повторить" : "Play"}
            </Button>
          )}
          {canEditOrEnter && (
            <Button size="sm" variant="ghost" onClick={() => setEditing((v) => !v)}>
              {editing ? "закрыть" : isRewrite ? "изменить" : "ввести счёт"}
            </Button>
          )}
        </div>
      </div>

      {ack && (
        <div
          role="status"
          className="caption"
          style={{
            marginTop: 6,
            color: ack.startsWith("Не")
              ? "var(--fg-danger)"
              : "#1E7A5A",
          }}
        >
          ✓ {ack}
        </div>
      )}
      {match.games.length > 0 && (
        <div className="caption" style={{ marginTop: 6, color: "var(--fg2)" }}>
          {match.games.map((g) => `${g.a}–${g.b}`).join(" · ")}
        </div>
      )}
      {isRewrite && parentLocked && (
        <div className="caption" style={{ marginTop: 4, color: "var(--fg3)" }}>
          Чтобы поменять результат, сначала правьте следующий матч.
        </div>
      )}

      {editing && canEditOrEnter && (
        <ResultForm
          tournamentId={tournamentId}
          match={match}
          isRewrite={isRewrite}
          onDone={() => setEditing(false)}
        />
      )}
    </div>
  );
}

function SideLabel({ team, isWinner }: { team: BracketTeam | null; isWinner: boolean }) {
  if (!team) return <span className="caption" style={{ color: "var(--fg3)" }}>TBD</span>;
  if (team.is_bye) return <span className="caption" style={{ color: "var(--fg3)" }}>BYE</span>;
  return (
    <span
      style={{
        fontWeight: 500,
        color: isWinner ? "var(--brand-orange)" : "var(--fg1)",
      }}
    >
      {team.name}
    </span>
  );
}

function ResultForm({
  tournamentId,
  match,
  isRewrite,
  onDone,
}: {
  tournamentId: number;
  match: BracketMatch;
  isRewrite: boolean;
  onDone: () => void;
}) {
  const admin = useAdminMatchResult(tournamentId);
  const [winner, setWinner] = useState<number | "">(
    isRewrite && match.winner_team_id != null ? match.winner_team_id : "",
  );
  const [games, setGames] = useState<{ a: string; b: string }[]>(() => {
    if (isRewrite && match.games.length > 0) {
      return match.games.map((g) => ({ a: String(g.a), b: String(g.b) }));
    }
    return [{ a: "", b: "" }];
  });

  const submit = async () => {
    if (winner === "") return;
    const parsed = games
      .filter((g) => g.a !== "" && g.b !== "")
      .map((g) => ({ a: Number(g.a), b: Number(g.b) }));
    try {
      await admin.mutateAsync({
        matchId: match.id,
        winner_team_id: Number(winner),
        games: parsed.length > 0 ? parsed : undefined,
        rewrite: isRewrite,
      });
      onDone();
    } catch {
      /* shown below */
    }
  };

  const live = isMatchLive(match);

  return (
    <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 12 }}>
      {live && (
        <div
          style={{
            background: "rgba(30,122,90,0.12)",
            color: "#1E7A5A",
            padding: "6px 12px",
            borderRadius: "var(--r-md)",
            fontSize: 13,
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          Матч идёт сейчас — игроки уведомлены.
        </div>
      )}
      <div className="label" style={{ color: "var(--fg2)", textAlign: "center" }}>Счёт:</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
        {games.map((g, idx) => (
          <div key={idx} style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center" }}>
            <ScoreInput value={g.a} onChange={(v) => update(idx, { a: v })} />
            <span style={{ color: "var(--fg2)", fontWeight: 700 }}>:</span>
            <ScoreInput value={g.b} onChange={(v) => update(idx, { b: v })} />
            {games.length > 1 && (
              <button
                type="button"
                onClick={() => setGames(games.filter((_, i) => i !== idx))}
                style={{ border: 0, background: "transparent", color: "var(--fg-danger)", cursor: "pointer" }}
              >
                убрать
              </button>
            )}
          </div>
        ))}
      </div>
      <div style={{ textAlign: "center" }}>
        <Button size="sm" variant="ghost" onClick={() => setGames([...games, { a: "", b: "" }])}>
          + игра
        </Button>
      </div>

      <WinnerToggle
        teamA={match.team_a}
        teamB={match.team_b}
        value={winner}
        onChange={setWinner}
      />

      {admin.error && (
        <div style={{ color: "var(--fg-danger)", fontSize: 13, textAlign: "center" }}>
          {(admin.error as Error).message}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
        <Button size="sm" onClick={submit} disabled={admin.isPending || winner === ""}>
          {admin.isPending ? "Сохраняем…" : "Сохранить"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Отмена</Button>
      </div>
    </div>
  );

  function update(idx: number, patch: Partial<{ a: string; b: string }>) {
    setGames(games.map((g, i) => (i === idx ? { ...g, ...patch } : g)));
  }
}

function WinnerToggle({
  teamA,
  teamB,
  value,
  onChange,
}: {
  teamA: BracketTeam | null;
  teamB: BracketTeam | null;
  value: number | "";
  onChange: (v: number) => void;
}) {
  const pick = (side: "a" | "b") => {
    if (side === "a" && teamA) onChange(teamA.id);
    if (side === "b" && teamB) onChange(teamB.id);
  };
  const active = (id: number | undefined) => id !== undefined && value === id;
  const pillStyle = (selected: boolean): React.CSSProperties => ({
    flex: 1,
    border: 0,
    padding: "8px 12px",
    background: selected ? "var(--brand-orange)" : "transparent",
    color: selected ? "#fff" : "var(--fg1)",
    borderRadius: "var(--r-md)",
    fontWeight: 600,
    fontSize: 14,
    cursor: "pointer",
    transition: "background 120ms ease",
  });
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
      <div className="label" style={{ color: "var(--fg2)" }}>Победил:</div>
      <div
        style={{
          display: "inline-flex",
          padding: 4,
          background: "var(--bg)",
          border: "1px solid var(--separator)",
          borderRadius: "var(--r-md)",
          gap: 4,
        }}
      >
        <button
          type="button"
          onClick={() => pick("a")}
          style={pillStyle(active(teamA?.id))}
        >
          {teamA?.name ?? "—"}
        </button>
        <button
          type="button"
          onClick={() => pick("b")}
          style={pillStyle(active(teamB?.id))}
        >
          {teamB?.name ?? "—"}
        </button>
      </div>
    </div>
  );
}

function ScoreInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <input
      type="number"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      min={0}
      max={99}
      {...noAutofill}
      style={{
        width: 54,
        border: "1px solid var(--separator)",
        borderRadius: "var(--r-sm)",
        padding: "6px 10px",
        textAlign: "center",
        fontFamily: "var(--font-display)",
        fontWeight: 700,
        fontSize: 15,
        background: "var(--bg)",
        color: "var(--fg1)",
      }}
    />
  );
}
